import React, { useState, useRef, useEffect } from 'react';
import { useTranslate } from '../hooks/useTranslate';
import { HiOutlineLanguage } from 'react-icons/hi2';
import { useLanguage } from '../contexts/LanguageContext';
import { useSupportedLanguages } from '../hooks/useSupportedLanguages';

interface LanguageSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const LanguageSelectorModal: React.FC<LanguageSelectorModalProps> = ({ isOpen, onClose }) => {
    const [search, setSearch] = useState('');
    const { activeLanguage, setLanguage } = useLanguage();
    const { languages, loading } = useSupportedLanguages();
    const { t } = useTranslate();
    const dialogRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Focus search input on open
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    // Escape to close
    useEffect(() => {
        if (!isOpen) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const filtered = languages.filter(l =>
        l.name.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (code: string) => {
        setLanguage(code);
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby="lang-modal-title"
                className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden notranslate"
            >
                {/* Header */}
                <div className="bg-gradient-to-r from-red-600 via-red-700 to-red-800 px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <HiOutlineLanguage className="w-7 h-7 text-white" />
                        <h2 id="lang-modal-title" className="text-lg font-semibold text-white">
                            {t('Change Language')}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center text-white transition-all duration-200"
                        aria-label="Close"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Search */}
                <div className="px-6 pt-4">
                    <input
                        ref={searchInputRef}
                        type="text"
                        placeholder={t('Search languages...')}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    />
                </div>

                {/* Language List */}
                <div className="px-6 py-4 max-h-80 overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-10" role="status" aria-label="Loading languages">
                            <svg className="animate-spin w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                            </svg>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-1.5">
                            {filtered.map((lang) => (
                                <button
                                    key={lang.code}
                                    onClick={() => handleSelect(lang.code)}
                                    className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-left text-sm transition-all duration-150 ${activeLanguage === lang.code
                                        ? 'bg-red-50 border border-red-200 text-red-700 font-medium'
                                        : 'hover:bg-gray-50 text-gray-700 border border-transparent'
                                        }`}
                                >
                                    <span className="text-xl">{lang.flag}</span>
                                    <span className="flex-1">{lang.name}</span>
                                    {activeLanguage === lang.code && (
                                        <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    )}
                                </button>
                            ))}
                            {filtered.length === 0 && (
                                <p className="text-sm text-gray-400 text-center py-6">{t('No languages found')}</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LanguageSelectorModal;
